import React, { useState, type ReactNode } from "react";
import { cn } from "@/utils/cn";
import type { Edge, TileRect } from "./canvasLayout";
import type { PanelId } from "./panels";
import { ReportSlotContext } from "./reportSlot";

interface ReportCanvasProps {
  tiles: ReadonlyArray<TileRect & { panel: PanelId }>;
  /** A panel by id; the canvas puts it in an open slot. */
  renderPanel: (id: PanelId) => ReactNode;
  titleOf: (id: PanelId) => string;
  onClose: (id: PanelId) => void;
  /** The panel waiting for an edge, while the person picks one. */
  placing: PanelId | null;
  /** A drag is carrying `placing`. The page hit-tests the pointer itself (a held mouse button sends no
   *  hover to the targets) and passes where a release would drop it. */
  dragging: boolean;
  dropAt: DropAt | null;
  edgesFor: (id: PanelId) => Edge[];
  onPlace: (target: PanelId, edge: Edge) => void;
  /** The tile a rail click fills in place of its panel, ringed until it is let go. */
  selected: PanelId | null;
  onSelect: (id: PanelId) => void;
  /** `placing` has no free spot: every tile offers itself to be replaced instead of its edges. */
  replacing: boolean;
  onReplace: (target: PanelId) => void;
  /** How many tiles this width holds, for the empty canvas's hint. */
  cap: number;
}

/** Where a drag would drop: an edge of a tile, or the empty canvas. */
export type DropAt = { target: PanelId; edge: Edge } | "empty";

const EDGES: ReadonlyArray<Edge> = ["left", "right", "top", "bottom"];

/** The drop under a point, read from the targets' data attributes. */
export function dropAtPoint(x: number, y: number, isPanel: (id: string) => id is PanelId): DropAt | null {
  const hit = document.elementFromPoint(x, y);
  if (!hit) return null;
  const target = hit.closest("[data-drop-edge]");
  if (target) {
    const panel = target.getAttribute("data-drop-target") ?? "";
    const edge = EDGES.find((e) => e === target.getAttribute("data-drop-edge"));
    return edge && isPanel(panel) ? { target: panel, edge } : null;
  }
  return hit.closest("[data-drop-empty]") ? "empty" : null;
}

/** Half a tile's gap: each tile pads by it and the layer reaches out by it, so tiles sit a gap apart and flush with the canvas. */
const HALF_GAP = 6;

const pct = (n: number) => `${n * 100}%`;

/** The hit area of each edge: with four edges, the triangle from the centre to that edge, so any point picks
 *  its nearest edge; with two, the half on that side. */
const HIT: Record<"four" | "two", Record<Edge, string>> = {
  four: {
    left: "polygon(0 0, 50% 50%, 0 100%)",
    right: "polygon(100% 0, 50% 50%, 100% 100%)",
    top: "polygon(0 0, 100% 0, 50% 50%)",
    bottom: "polygon(0 100%, 100% 100%, 50% 50%)",
  },
  two: {
    left: "inset(0 50% 0 0)",
    right: "inset(0 0 0 50%)",
    top: "inset(0 0 50% 0)",
    bottom: "inset(50% 0 0 0)",
  },
};

/** Where the new panel lands: the half of the tile on that edge. */
const LANDING: Record<Edge, string> = {
  left: "left-0 top-0 bottom-0 w-1/2",
  right: "right-0 top-0 bottom-0 w-1/2",
  top: "left-0 right-0 top-0 h-1/2",
  bottom: "left-0 right-0 bottom-0 h-1/2",
};

/** Each edge's label, centred on it. */
const LABEL_SPOT: Record<Edge, string> = {
  left: "left-3 top-1/2 -translate-y-1/2",
  right: "right-3 top-1/2 -translate-y-1/2",
  top: "top-3 left-1/2 -translate-x-1/2",
  bottom: "bottom-3 left-1/2 -translate-x-1/2",
};

const EDGE_LABEL: Record<Edge, string> = { left: "Left", right: "Right", top: "Top", bottom: "Bottom" };

/** A click on these inside a tile is the panel's own (a pip, a link, a month select), not a pick of the tile. */
const OWN_CONTROLS = "button, a, input, select, textarea, label, [role='button'], [role='dialog']";

/** On a full canvas, the whole tile as one target: the panel being placed takes this tile's place. */
const ReplaceTarget: React.FC<{
  target: PanelId;
  targetTitle: string;
  placingTitle: string;
  onReplace: (target: PanelId) => void;
}> = ({ target, targetTitle, placingTitle, onReplace }) => {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      aria-label={`Open ${placingTitle} in place of ${targetTitle}`}
      className="absolute inset-0 z-10 cursor-copy rounded-2xl focus:outline-hidden"
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      onClick={() => onReplace(target)}
    >
      <span
        className={cn(
          "absolute inset-0 grid place-items-center rounded-2xl border-2 border-dashed transition-colors",
          hover ? "bg-blue-600/12 border-blue-600" : "bg-white/40 border-blue-300"
        )}
      >
        <span
          className={cn(
            "rounded-full px-3 py-1.5 text-[13px] font-medium shadow-sm",
            hover ? "bg-blue-600 text-white" : "bg-white text-blue-700 ring-1 ring-blue-200"
          )}
        >
          {hover ? `${placingTitle} opens here` : `Replace ${targetTitle}`}
        </span>
      </span>
    </button>
  );
};

const DropTargets: React.FC<{
  target: PanelId;
  targetTitle: string;
  placingTitle: string;
  edges: Edge[];
  /** Set while dragging: the edge under the pointer, as the page hit-tested it. */
  dragEdge: Edge | null;
  dragging: boolean;
  onPlace: (target: PanelId, edge: Edge) => void;
}> = ({ target, targetTitle, placingTitle, edges, dragEdge, dragging, onPlace }) => {
  const [pointerEdge, setHover] = useState<Edge | null>(null);
  const hover = dragging ? dragEdge : pointerEdge;
  if (edges.length === 0) {
    // A quarter does not split: muted, and it takes no clicks.
    return <div className="absolute inset-0 z-10 rounded-2xl bg-gray-100/70" />;
  }
  const hit = HIT[edges.length === 4 ? "four" : "two"];
  // No wash over the tile: it stays at full strength, and only the half the drop takes is marked.
  return (
    <div className="absolute inset-0 z-10 rounded-2xl" onPointerLeave={() => setHover(null)}>
      {hover && (
        <div
          data-landing={hover}
          className={cn(
            "absolute grid place-items-center rounded-2xl bg-blue-600/12 border-2 border-dashed border-blue-600 pointer-events-none",
            LANDING[hover]
          )}
        >
          <span className="rounded-full bg-blue-600 px-3 py-1.5 text-[13px] font-medium text-white shadow-sm">
            {placingTitle} opens here
          </span>
        </div>
      )}
      {edges.map((edge) => (
        <button
          key={edge}
          type="button"
          data-drop-edge={edge}
          data-drop-target={target}
          aria-label={`Open ${placingTitle} ${edge === "left" || edge === "right" ? `to the ${edge} of` : edge === "top" ? "above" : "below"} ${targetTitle}`}
          style={{ clipPath: hit[edge] }}
          className="absolute inset-0 cursor-copy focus:outline-hidden"
          onPointerEnter={() => setHover(edge)}
          onFocus={() => setHover(edge)}
          onBlur={() => setHover((h) => (h === edge ? null : h))}
          onClick={() => onPlace(target, edge)}
        />
      ))}
      {/* Once an edge is picked its landing says it all, so the edge labels step aside. */}
      {edges.map((edge) =>
        hover ? null : (
          <span
            key={edge}
            className={cn(
              "absolute pointer-events-none rounded-full bg-white px-2.5 py-1 text-[12px] font-medium text-blue-700 ring-1 ring-blue-200",
              LABEL_SPOT[edge]
            )}
          >
            {EDGE_LABEL[edge]}
          </span>
        )
      )}
    </div>
  );
};

/** The opened panels, one to four tiles in halves and quarters, each a
 *  WorkspacePanel filling its tile, its charts taking the spare height and
 *  its body scrolling inside past the tile. A click on a tile picks it for the rail to fill.
 *  While a panel is dragged, every tile that can split shows its edges; on a
 *  full canvas every tile offers its place instead. */
export const ReportCanvas: React.FC<ReportCanvasProps> = ({
  tiles,
  renderPanel,
  titleOf,
  onClose,
  placing,
  dragging,
  dropAt,
  edgesFor,
  onPlace,
  selected,
  onSelect,
  replacing,
  onReplace,
  cap,
}) => {
  if (tiles.length === 0) {
    return (
      <div
        data-drop-empty=""
        className={cn(
          "h-full rounded-2xl border-2 border-dashed grid place-items-center text-center px-6 transition-colors",
          dropAt === "empty" ? "border-blue-500 bg-blue-50" : dragging ? "border-blue-300" : "border-gray-300"
        )}
      >
        <div>
          <p className="text-[15px] font-medium text-gray-700">{dragging && placing ? `Drop to open ${titleOf(placing)}` : "Nothing open"}</p>
          <p className="mt-1 text-[13px] text-gray-500">
            Click a panel on the left to open it here, or drag it in. Up to {cap === 2 ? "two" : "four"}, side by side or stacked.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="relative h-full">
      <div className="absolute" style={{ inset: -HALF_GAP }}>
        {tiles.map(({ panel, x, y, width, height }) => (
          <div
            key={panel}
            data-tile={panel}
            className="absolute transition-[left,top,width,height] duration-200 ease-out"
            style={{ left: pct(x), top: pct(y), width: pct(width), height: pct(height), padding: HALF_GAP }}
          >
            <div className="relative h-full">
              {/* The card fills the tile, its charts growing into the height the rest leaves. A click on it,
                  outside the panel's own controls, picks the tile for the rail to fill. */}
              <div
                data-selected={selected === panel ? "" : undefined}
                className={cn(
                  "flex flex-col h-full rounded-2xl transition-shadow duration-300",
                  selected === panel && "ring-2 ring-blue-500 ring-offset-2 ring-offset-gray-100"
                )}
                onClick={(e) => {
                  // A portalled pip card or drawer bubbles here through React but sits outside the tile's DOM.
                  const own = e.target instanceof Element && e.currentTarget.contains(e.target) && !e.target.closest(OWN_CONTROLS);
                  if (!placing && own) onSelect(panel);
                }}
              >
                <ReportSlotContext.Provider value={{ kind: "tile", onClose: () => onClose(panel) }}>
                  {renderPanel(panel)}
                </ReportSlotContext.Provider>
              </div>
              {placing && replacing && (
                <ReplaceTarget target={panel} targetTitle={titleOf(panel)} placingTitle={titleOf(placing)} onReplace={onReplace} />
              )}
              {placing && !replacing && (
                <DropTargets
                  target={panel}
                  targetTitle={titleOf(panel)}
                  placingTitle={titleOf(placing)}
                  edges={edgesFor(panel)}
                  dragging={dragging}
                  dragEdge={dropAt !== null && dropAt !== "empty" && dropAt.target === panel ? dropAt.edge : null}
                  onPlace={onPlace}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
